/**
 * Promise-based, in-app replacements for the browser-native
 * window.confirm / window.prompt / window.alert dialogs.
 *
 * A single <ConfirmDialogHost/> is mounted once at the app root
 * (see App.js). Anywhere in the tree you can:
 *
 *   if (!(await confirmDialog('Delete this?'))) return;
 *   const name = await promptDialog({ title: 'Rename', defaultValue: cur });
 *   await alertDialog('Heads up…');
 *
 * confirmDialog → resolves boolean (true = confirmed)
 * promptDialog  → resolves string, or null when cancelled
 * alertDialog   → resolves void once acknowledged
 *
 * Each accepts either a plain string (used as the body) or an options
 * object: { title, description, confirmText, cancelText, destructive,
 * defaultValue, placeholder, multiline }.
 */
import * as React from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, HelpCircle, Info, Pencil } from 'lucide-react';

let emit = null;
let seq = 0;

function normalize(opts) {
  if (opts == null) return {};
  if (typeof opts === 'string') return { description: opts };
  return opts;
}

function request(kind, opts) {
  const o = normalize(opts);
  return new Promise((resolve) => {
    if (emit) {
      emit({ id: ++seq, kind, resolve, opts: o });
      return;
    }
    // Host not mounted (should never happen in-app) — fall back so a
    // caller never hangs forever waiting on a dropped promise.
    if (kind === 'confirm') resolve(window.confirm(o.description || o.title || ''));
    else if (kind === 'prompt') resolve(window.prompt(o.description || o.title || '', o.defaultValue || ''));
    else { window.alert(o.description || o.title || ''); resolve(); }
  });
}

export const confirmDialog = (opts) => request('confirm', opts);
export const promptDialog = (opts) => request('prompt', opts);
export const alertDialog = (opts) => request('alert', opts);

export function ConfirmDialogHost() {
  const [queue, setQueue] = React.useState([]);
  const [value, setValue] = React.useState('');
  const finishedRef = React.useRef(0);
  const current = queue[0] || null;

  React.useEffect(() => {
    emit = (item) => setQueue((q) => [...q, item]);
    return () => { emit = null; };
  }, []);

  const currentId = current?.id;
  React.useEffect(() => {
    if (current && current.kind === 'prompt') {
      setValue(current.opts.defaultValue || '');
    }
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = (result) => {
    if (!current) return;
    if (finishedRef.current === current.id) return; // guard double-fire
    finishedRef.current = current.id;
    current.resolve(result);
    setQueue((q) => q.slice(1));
  };

  if (!current) return null;

  const o = current.opts;

  if (current.kind === 'prompt') {
    const Field = o.multiline ? Textarea : Input;
    return (
      <Dialog open onOpenChange={(open) => { if (!open) finish(null); }}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="app-prompt-dialog">
          <DialogHeader className="border-b border-[#E2E4E8] pb-3">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#4575A8]/10">
                <Pencil className="w-4 h-4 text-[#4575A8]" />
              </span>
              <DialogTitle className="text-xl text-foreground" style={{ fontFamily: "'Exo 2', system-ui, sans-serif" }}>
                {o.title || 'Enter a value'}
              </DialogTitle>
            </div>
            {o.description && (
              <DialogDescription className="text-xs text-[#5C6370] whitespace-pre-line">
                {o.description}
              </DialogDescription>
            )}
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); finish(value); }}
            className="py-1"
          >
            <Field
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={o.placeholder || ''}
              className="bg-muted border-border text-foreground"
              rows={o.multiline ? 3 : undefined}
              data-testid="app-prompt-input"
            />
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => finish(null)}
                className="border-border"
                data-testid="app-prompt-cancel"
              >
                {o.cancelText || 'Cancel'}
              </Button>
              <Button
                type="submit"
                className="bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
                data-testid="app-prompt-confirm"
              >
                {o.confirmText || 'OK'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  const isConfirm = current.kind === 'confirm';
  const destructive = !!o.destructive;
  const HeaderIcon = destructive ? AlertTriangle : (isConfirm ? HelpCircle : Info);
  const chipCls = destructive ? 'bg-[#B4463A]/10' : 'bg-[#4575A8]/10';
  const iconCls = destructive ? 'text-[#B4463A]' : 'text-[#4575A8]';
  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) finish(isConfirm ? false : undefined); }}>
      <AlertDialogContent className="bg-card border-border" data-testid="app-confirm-dialog">
        <AlertDialogHeader className="border-b border-[#E2E4E8] pb-3">
          <div className="flex items-center gap-2.5">
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${chipCls}`}>
              <HeaderIcon className={`w-4 h-4 ${iconCls}`} />
            </span>
            <AlertDialogTitle className="text-xl text-foreground" style={{ fontFamily: "'Exo 2', system-ui, sans-serif" }}>
              {o.title || (isConfirm ? 'Are you sure?' : 'Notice')}
            </AlertDialogTitle>
          </div>
          {o.description && (
            <AlertDialogDescription className="text-xs text-[#5C6370] whitespace-pre-line">
              {o.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {isConfirm && (
            <Button
              type="button"
              variant="outline"
              onClick={() => finish(false)}
              className="border-border"
              data-testid="app-confirm-cancel"
            >
              {o.cancelText || 'Cancel'}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => finish(isConfirm ? true : undefined)}
            className={o.destructive
              ? 'bg-[#B4463A] hover:bg-[#B4463A]/90 text-white'
              : 'bg-[#4575A8] hover:bg-[#5C8AC2] text-white'}
            data-testid="app-confirm-accept"
          >
            {o.confirmText || (isConfirm ? 'Continue' : 'OK')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
